import React from 'react'
import styled from 'styled-components'

interface FontData {
  weight: string
  size: string
  line: string
  spacing: string
}

enum FontStyle {
  SuperTiny = 'superTiny',
  ExTiny = 'exTiny',
  Tiny = 'tiny',
  bTiny = 'btiny',
  TinySmall = 'tinySmall',
  bTinySmall = 'bTinySmall',
  Small = 'small',
  bSmall = 'bSmall',
  Normal = 'normal',
  Medium = 'medium',
  MediumBold = 'mediumBold',
  Large = 'large',
  ExLarge = 'exLarge',
  SuperLarge = 'superLarge',
  Button = 'button',
  ButtonBold = 'buttonBold',
  Default = 'default',
}

interface FontStyleMap {
  [FontStyle.SuperTiny]: FontData
  [FontStyle.ExTiny]: FontData
  [FontStyle.Tiny]: FontData
  [FontStyle.bTiny]: FontData
  [FontStyle.TinySmall]: FontData
  [FontStyle.bTinySmall]: FontData
  [FontStyle.Small]: FontData
  [FontStyle.bSmall]: FontData
  [FontStyle.Normal]: FontData
  [FontStyle.Medium]: FontData
  [FontStyle.MediumBold]: FontData
  [FontStyle.Large]: FontData
  [FontStyle.ExLarge]: FontData
  [FontStyle.SuperLarge]: FontData
  [FontStyle.Button]: FontData
  [FontStyle.ButtonBold]: FontData
  [FontStyle.Default]: FontData
}

const StyleMap: FontStyleMap = {
  superTiny: {
    weight: '800',
    size: '8px',
    line: '10px',
    spacing: '0.03em',
  },
  exTiny: {
    weight: '700',
    size: '11px',
    line: '13px',
    spacing: '0.03em',
  },
  tiny: {
    weight: '400',
    size: '11px',
    line: '14px',
    spacing: 'normal',
  },
  btiny: {
    weight: '700',
    size: '11px',
    line: '14px',
    spacing: 'normal',
  },
  tinySmall: {
    weight: '400',
    size: '12px',
    line: '15px',
    spacing: 'normal',
  },
  bTinySmall: {
    weight: '700',
    size: '12px',
    line: '15px',
    spacing: 'normal',
  },
  small: {
    weight: 'normal',
    size: '13px',
    line: '16px',
    spacing: 'normal',
  },
  bSmall: {
    weight: '600',
    size: '13px',
    line: '16px',
    spacing: 'normal',
  },
  button: {
    weight: '600',
    size: '14px',
    line: '17px',
    spacing: 'normal',
  },
  buttonBold: {
    weight: 'bold',
    size: '14px',
    line: '17px',
    spacing: 'normal',
  },
  normal: {
    weight: '400',
    size: '16px',
    line: '19px',
    spacing: 'normal',
  },
  medium: {
    weight: 'normal',
    size: '20px',
    line: '24px',
    spacing: 'normal',
  },
  mediumBold: {
    weight: '700',
    size: '20px',
    line: '22px',
    spacing: 'normal',
  },
  large: {
    weight: 'bold',
    size: '24px',
    line: '29px',
    spacing: 'normal',
  },
  exLarge: {
    weight: 'bold',
    size: '32px',
    line: '39px',
    spacing: 'normal',
  },
  superLarge: {
    weight: 'bold',
    size: '48px',
    line: '58px',
    spacing: 'normal',
  },
  default: {
    weight: 'normal',
    size: '17px',
    line: '20px',
    spacing: 'normal',
  },
}

interface TextProps {
  align?: string
  as?: 'p' | 'span'
  children?: React.ReactNode
  color?: string
  fontStyle?: FontStyle
  size?: FontStyle //backwards compatability
  text?: string
  weight?: string | number
  width?: string | number
  selectionDisabled?: boolean
  style?: React.CSSProperties
  onClick?: () => void
  class?: string
}

const getFontStyleDataOrDefault = (
  style: FontStyle | string = FontStyle.Default,
): FontData => {
  const key = style as FontStyle
  if (!(key in StyleMap)) return StyleMap[FontStyle.Default]

  return { ...StyleMap[key] }
}

interface StyleTextProps {
  $fontData: FontData
  $color?: string
  $align?: string
  $width?: string
  $selectionDisabled?: boolean
}

const StyledText = styled.p<StyleTextProps>`
  text-align: ${({ $align }) => $align ?? 'left'};
  color: ${({ $color }) => $color ?? 'var(--chakra-colors-fg-default)'};
  font-family: 'Inter', Helvetica, sans-serif;
  font-style: normal;
  font-weight: ${({ $fontData }) => $fontData.weight};
  font-size: ${({ $fontData }) => $fontData.size};
  line-height: ${({ $fontData }) => $fontData.line};
  letter-spacing: ${({ $fontData }) => $fontData.spacing};
  margin: 0;
  width: ${({ $width }) => $width ?? 'auto'};
  user-select: ${({ $selectionDisabled }) =>
    $selectionDisabled ? 'none' : 'auto'};
`
/**
 * The core Text component for Mango applications.
 * @param {FontStyle} fontStyle Used to convey the size and style information compactly of the
 * Text you are rendering.
 * @param {FontStyle} size Only used for backwards compatability prior to Typescript
 * conversion.  If you are making a new component, use the fontStyle parameter, as size
 * will eventually be removed.
 */
const Text = ({
  align = 'left',
  color = 'var(--chakra-colors-fg-default)',
  fontStyle = FontStyle.Normal,
  size,
  text = '',
  weight: weightOverride,
  width = 'auto',
  selectionDisabled = false,
  style,
  children,
  ...props
}: TextProps) => {
  const selectedStyle = size ?? fontStyle
  const fontData = getFontStyleDataOrDefault(selectedStyle)

  fontData.weight = weightOverride?.toString() ?? fontData.weight
  return (
    <StyledText
      $align={align}
      $fontData={fontData}
      $color={color}
      $width={width.toString()}
      $selectionDisabled={selectionDisabled}
      style={{ ...style }}
      {...props}
    >
      {children ?? text}
    </StyledText>
  )
}

export default Text
export { FontStyle, StyledText as TextElement }
export type { TextProps }
